import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import moment from 'moment';
import config from '../../../config';
import DatacoLoadingIndicator from '../../_components/DatacoLoadingIndicator';
import { getStoredUser, getSessionHeaders, readJsonResponse } from './tpmRequest';

const DEFAULT_ROWS_PER_PAGE = 10;

const STATUS_FILTERS = [
    { value: '', label: 'All' },
    { value: 'Opened', label: 'Opened' },
    { value: 'Closed', label: 'Closed' }
];

const TIER_FILTERS = [
    { value: '', label: 'All tiers' },
    { value: 'A', label: 'Tier A' },
    { value: 'B', label: 'Tier B' },
    { value: 'C', label: 'Tier C' }
];

function getRows(body) {
    if (Array.isArray(body)) return body;
    return body && Array.isArray(body.data) ? body.data : [];
}

function getTotalRows(body, rows) {
    if (body && body.meta && Number.isFinite(Number(body.meta.total_count))) return Number(body.meta.total_count);
    return rows.length;
}

function formatDate(value) {
    if (!value) return '';
    const date = moment(value);
    return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : String(value);
}

function formatNumber(value) {
    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue.toLocaleString() : '';
}

export function TpmStatusBadge({ status }) {
    const isClosed = String(status) === 'Closed';
    return (
        <span
            style={{
                color: isClosed ? '#218838' : '#c77800',
                fontWeight: 700
            }}
        >
            {status || 'Opened'}
        </span>
    );
}

export default function TpmMaintenanceList() {
    const [keyword, setKeyword] = useState('');
    const [activeKeyword, setActiveKeyword] = useState('');
    const [status, setStatus] = useState('');
    const [tier, setTier] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const requestSequence = useRef(0);

    const loadRecords = useCallback((page, limit, searchKeyword, statusFilter, tierFilter) => {
        const user = getStoredUser();
        if (!user || !user.accountId) {
            setRows([]);
            setTotalRows(0);
            setError('Please sign in to view TPM maintenance records.');
            return Promise.resolve();
        }

        const offset = (page - 1) * limit;
        const requestId = ++requestSequence.current;
        const params = new URLSearchParams({
            keyword: searchKeyword || '',
            status: statusFilter || '',
            tier: tierFilter || '',
            limit: String(limit),
            offset: String(offset)
        });

        setLoading(true);
        setError('');
        return fetch(`${config.apiUrl}/getTpmMaintenanceByCompany?${params.toString()}`, {
            method: 'POST',
            headers: getSessionHeaders(user)
        })
            .then(readJsonResponse)
            .then(body => {
                if (requestId !== requestSequence.current) return;
                const records = getRows(body);
                setRows(records);
                setTotalRows(getTotalRows(body, records));
                setLoading(false);
            })
            .catch(requestError => {
                if (requestId !== requestSequence.current) return;
                setRows([]);
                setTotalRows(0);
                setError(requestError.message || 'Failed to load TPM maintenance records.');
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        loadRecords(currentPage, rowsPerPage, activeKeyword, status, tier);
    }, [activeKeyword, currentPage, loadRecords, rowsPerPage, status, tier]);

    const columns = [
        { name: 'Tier', selector: row => row.tier || '', width: '80px' },
        { name: 'Part No', selector: row => row.partNo || '' },
        { name: 'Tooling No', selector: row => row.toolingNo || '' },
        { name: 'Interval PCS', selector: row => formatNumber(row.intervalPcs), right: true },
        { name: 'Triggered At PCS', selector: row => formatNumber(row.triggeredAtPcs), right: true },
        { name: 'Triggered', selector: row => formatDate(row.createdAt || row.created_at) },
        { name: 'Repair Ticket', selector: row => row.repairTicketId || '' },
        { name: 'Status', cell: row => <TpmStatusBadge status={row.status} />, width: '110px' },
        {
            name: '',
            cell: row => <Link to={`/tooling/tpm/maintenance/${row.id}`}>View</Link>,
            ignoreRowClick: true,
            allowOverflow: true,
            width: '80px'
        }
    ];

    const applyFilter = (setter) => (event) => {
        setCurrentPage(1);
        setter(event.target.value);
    };

    return (
        <div>
            {error && <div className="alert alert-danger" role="alert">{error}</div>}
            <Form
                className="d-flex gap-2 flex-wrap mb-3"
                onSubmit={event => {
                    event.preventDefault();
                    setCurrentPage(1);
                    setActiveKeyword(keyword);
                }}
            >
                <Form.Control
                    aria-label="Search TPM maintenance records"
                    value={keyword}
                    placeholder="Search part or tooling no..."
                    onChange={event => setKeyword(event.target.value)}
                    style={{ maxWidth: '260px' }}
                />
                <Form.Select
                    aria-label="Filter by status"
                    value={status}
                    onChange={applyFilter(setStatus)}
                    style={{ maxWidth: '150px' }}
                >
                    {STATUS_FILTERS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </Form.Select>
                <Form.Select
                    aria-label="Filter by tier"
                    value={tier}
                    onChange={applyFilter(setTier)}
                    style={{ maxWidth: '150px' }}
                >
                    {TIER_FILTERS.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </Form.Select>
                <Button type="submit">Search</Button>
            </Form>
            <DataTable
                columns={columns}
                data={rows}
                progressPending={loading}
                progressComponent={<DatacoLoadingIndicator loading={loading} size="compact" label="Loading TPM maintenance records" />}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={rowsPerPage}
                paginationDefaultPage={currentPage}
                onChangePage={page => setCurrentPage(page)}
                onChangeRowsPerPage={(limit, page) => {
                    setRowsPerPage(limit);
                    setCurrentPage(page);
                }}
                noDataComponent="No TPM maintenance records yet."
                highlightOnHover
            />
        </div>
    );
}
